import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useApi } from "@/lib/api";

const EDMONTON = "America/Edmonton";
// "November 19th". Only used for plain YYYY-MM-DD dates.
const fmtLongDay = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
    if (!m) return "";
    const d = +m[3];
    const month = new Date(Date.UTC(+m[1], +m[2] - 1, d, 12)).toLocaleDateString("en-US", {
        timeZone: "UTC",
        month: "long",
    });
    const tens = d % 100;
    const suffix =
        tens >= 11 && tens <= 13 ? "th" : { 1: "st", 2: "nd", 3: "rd" }[d % 10] || "th";
    return `${month} ${d}${suffix}`;
};

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
        if (m) {
            const [, y, mo, d] = m;
            const dt = new Date(Date.UTC(+y, +mo - 1, +d, 12, 0, 0));
            return dt.toLocaleDateString("en-US", {
                timeZone: "UTC",
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        }
        return new Date(iso).toLocaleDateString("en-US", {
            timeZone: EDMONTON,
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return "";
    }
};

function StateChip({ state, building }) {
    if (building) {
        return (
            <span
                data-testid="topic-state-chip"
                className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase rounded-full px-3 py-1 whitespace-nowrap"
                style={{ background: "var(--color-electric)", color: "#FFFFFF" }}
            >
                Tracking
            </span>
        );
    }
    const palettes = {
        named: ["var(--color-named-bg)", "var(--color-named-fg)", "Named"],
        growing: ["var(--color-growing-bg)", "var(--color-growing-fg)", "Growing"],
        not_yet: ["var(--color-line)", "var(--color-muted)", "Not yet"],
    };
    const [bg, fg, label] = palettes[state] || palettes.not_yet;
    return (
        <span
            data-testid="topic-state-chip"
            className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase rounded-full px-3 py-1 whitespace-nowrap"
            style={{ background: bg, color: fg }}
        >
            {label}
        </span>
    );
}

function BarChart({ bars }) {
    const maxAnswers = Math.max(1, ...bars.map((b) => b.answers || 0));
    const H = 128;
    return (
        <div className="grid grid-cols-4 gap-3 items-end" data-testid="topic-bars">
            {bars.map((b, idx) => {
                const px = b.answers
                    ? Math.max(4, Math.round((b.named / maxAnswers) * H))
                    : 0;
                const kind = b.kind;
                let barStyle = {};
                if (kind === "past" || kind === "current") {
                    if (b.named > 0) {
                        barStyle = {
                            background: "var(--color-signal)",
                            height: px || 4,
                        };
                    } else {
                        barStyle = {
                            background: "var(--color-line-dark)",
                            height: 4,
                        };
                    }
                } else if (kind === "next") {
                    barStyle = {
                        border: "1.5px dashed var(--color-electric)",
                        height: H,
                        background: "transparent",
                    };
                } else {
                    barStyle = {
                        border: "1.5px dashed var(--color-line-dark)",
                        height: H,
                        background: "transparent",
                    };
                }
                return (
                    <div
                        key={b.month}
                        data-testid={`topic-bar-${idx}`}
                        className="flex flex-col items-stretch gap-1 min-w-0"
                    >
                        <div
                            className="text-paper font-display font-black text-[13px] h-4 text-center"
                            style={{
                                color:
                                    kind === "past" || kind === "current"
                                        ? "var(--color-paper)"
                                        : "var(--color-on-ink-muted)",
                            }}
                        >
                            {(kind === "past" || kind === "current") && b.named > 0
                                ? b.named
                                : ""}
                        </div>
                        <div
                            className="rounded-md"
                            style={{
                                ...barStyle,
                                minHeight: 4,
                            }}
                        />
                        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-on-ink-muted text-center">
                            {b.label}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function DashboardTopic() {
    const { slug } = useParams();
    const { request } = useApi();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const viewAsId = params.get("view_as") || "";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const url = viewAsId
                ? `/admin/dashboard/${encodeURIComponent(viewAsId)}/topic/${encodeURIComponent(slug)}`
                : `/dashboard/topic/${encodeURIComponent(slug)}`;
            const res = await request(url);
            setData(res);
        } catch (err) {
            if (err?.status === 404) {
                toast.error("Topic not found.");
                navigate("/dashboard", { replace: true });
                return;
            }
            toast.error(err?.message || "Could not load topic.");
        } finally {
            setLoading(false);
        }
    }, [request, slug, viewAsId, navigate]);

    useEffect(() => {
        load();
    }, [load]);

    const backHref = viewAsId
        ? `/dashboard?view_as=${encodeURIComponent(viewAsId)}`
        : "/dashboard";

    const stateSentence = useMemo(() => {
        if (!data) return "";
        if (data.state === "named") {
            return `When people look for ${data.topic.toLowerCase()} in ${data.city}, you're now one of the names that comes up.`;
        }
        if (data.state === "growing") {
            return `You're showing up around ${data.topic.toLowerCase()}. Being named is next.`;
        }
        const by = fmtLongDay(data.next_check_date);
        return `We're working on getting you named in this topic. Check back ${by ? `by ${by}` : "next month"} to see how you're ranking.`;
    }, [data]);

    if (loading) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center text-sm" style={{ color: "var(--color-muted)" }}>
                Loading...
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="min-h-screen bg-paper">
            <div className="mx-auto w-full max-w-[480px] px-5 pt-5 pb-3 flex items-center justify-between">
                <Link
                    to={backHref}
                    data-testid="topic-back"
                    aria-label="Back to dashboard"
                    className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-surface border"
                    style={{ borderColor: "var(--color-line)" }}
                >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden>
                        <path
                            d="M15 6l-6 6 6 6"
                            fill="none"
                            stroke="var(--color-ink)"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </Link>
                <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase" style={{ color: "var(--color-muted)" }}>
                    WHAT YOU'RE KNOWN FOR
                </div>
                <div className="w-11" aria-hidden />
            </div>
            <main className="mx-auto w-full max-w-[480px] px-5 pb-16 flex flex-col gap-5">
                <section className="pt-2 flex flex-col gap-3">
                    <StateChip state={data.state} building={data.building_mode} />
                    <h1
                        data-testid="topic-headline"
                        className="font-display font-black tracking-tight text-ink text-[44px] leading-[1.02]"
                    >
                        {data.topic}
                    </h1>
                    <p
                        data-testid="topic-state-sentence"
                        className="text-[15px]"
                        style={{ color: "var(--color-muted)" }}
                    >
                        {stateSentence}
                    </p>
                </section>

                {/* Named by month tile */}
                <section
                    data-testid="topic-named-month"
                    className="bg-ink rounded-[28px] p-5 sm:p-6 flex flex-col gap-4"
                >
                    <div className="flex items-center justify-between gap-3">
                        <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase text-on-ink-muted">
                            NAMED, BY MONTH
                        </div>
                        <div
                            data-testid="topic-month-score"
                            className="font-display font-black text-paper text-[18px]"
                        >
                            {data.named_this_month} of {data.answers_this_month} in {data.month_label}
                        </div>
                    </div>
                    <BarChart bars={data.bars} />
                    <div className="text-on-ink-muted text-[13px]">
                        Out of {data.searches_per_month} searches we run for this topic each month.
                    </div>
                </section>

                {/* What helped tile */}
                {!data.building_mode && (
                    <section
                        data-testid="topic-what-helped"
                        className="bg-ink rounded-[28px] p-5 sm:p-6 flex flex-col gap-3"
                    >
                        <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase text-on-ink-muted">
                            WHAT HELPED
                        </div>
                        <HelpedRow
                            label={`Page sections about ${data.topic.toLowerCase()}`}
                            value={`${data.what_helped.page_sections} on your page`}
                            testid="topic-helped-page"
                        />
                        <HelpedRow
                            label="Placements covering this topic"
                            value={`${data.what_helped.live_placements} live`}
                            testid="topic-helped-placements"
                        />
                    </section>
                )}

                {/* Tip tile (electric) */}
                <section
                    data-testid="topic-tip"
                    className="rounded-[28px] p-5 sm:p-6 flex flex-col gap-3"
                    style={{ background: "var(--color-electric)", color: "#FFFFFF" }}
                >
                    <div className="font-mono font-medium text-[11px] tracking-[0.14em] uppercase" style={{ color: "#FFFFFF" }}>
                        TIP
                    </div>
                    <p className="font-display text-[17px] leading-snug">
                        {data.tip}
                    </p>
                </section>
            </main>
        </div>
    );
}

function HelpedRow({ label, value, testid }) {
    return (
        <div
            data-testid={testid}
            className="rounded-[18px] p-4 flex items-center gap-3"
            style={{ background: "var(--color-ink-raised)" }}
        >
            <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0"
                style={{ background: "var(--color-signal)" }}
                aria-hidden
            >
                <svg viewBox="0 0 24 24" className="w-4 h-4">
                    <path
                        d="M5 12.5l4.5 4.5L19 7"
                        fill="none"
                        stroke="var(--color-ink)"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
            <div className="min-w-0 flex-1">
                <div className="font-display text-paper text-[15px] font-bold">
                    {label}
                </div>
                <div className="text-on-ink-muted text-[13px]">{value}</div>
            </div>
        </div>
    );
}
